import { useNavigate } from 'react-router-dom';
import { useUrlParams } from '../hooks/useUrlParams';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  App as AntdApp,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import {
  deleteAircraft,
  listAircraft,
  listOperators,
} from '../api/endpoints';
import type {
  Aircraft,
  AircraftCategory,
  Status,
} from '../api/types';
import { AIRCRAFT_CATEGORIES, CATEGORY_LABELS } from '../api/types';

const { Title, Text } = Typography;

// Regression 28 Sep — the sum of the column widths below; narrower windows
// scroll the table, not the page.
// Sized to fit a 1440px window without scrolling.
const AIRCRAFT_TABLE_WIDTH = 210 + 130 + 70 + 80 + 90 + 100 + 140 + 100 + 100 + 90;

export function AircraftListPage() {
  const nav = useNavigate();
  // SHAR-1010 put the page number in the URL; SHAR-1076 - the filters too, so
  // "Back" from an aircraft (the form goes back in history) returns to the list
  // exactly as it was: tail number, category, operator, status and page.
  // Written with replace, as the page is: typing a tail number must not add a
  // history entry per key.
  const [searchParams, setParams] = useUrlParams({ replace: true });
  const tail = searchParams.get('tail') ?? '';
  const category = (searchParams.get('category') || undefined) as AircraftCategory | undefined;
  const operatorId = searchParams.get('operator') || undefined;
  const status = (searchParams.get('status') || undefined) as Status | undefined;
  const setTail = (v: string) => setParams({ tail: v || null });
  const setCategory = (v: AircraftCategory | undefined) => setParams({ category: v ?? null });
  const setOperatorId = (v: string | undefined) => setParams({ operator: v ?? null });
  const setStatus = (v: Status | undefined) => setParams({ status: v ?? null });
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1);
  const setPage = (next: number) => setParams({ page: next <= 1 ? null : String(next) });
  const pageSize = 10;

  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['aircraft', tail, category, operatorId, status, page],
    queryFn: () =>
      listAircraft({
        tail: tail || undefined,
        category,
        operatorId,
        status,
        page,
        pageSize,
      }),
  });
  const operators = useQuery({
    queryKey: ['operators', 'all-for-aircraft-filter'],
    queryFn: () => listOperators({ page: 1, pageSize: 100 }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAircraft,
    onSuccess: () => {
      message.success('Aircraft deleted');
      qc.invalidateQueries({ queryKey: ['aircraft'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Aircraft</Title>
          <Text type="secondary">Every aircraft visible to ShareJet, grouped by operator.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => nav('/aircraft/new')}>
          New aircraft
        </Button>
      </div>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search tail number or model"
            value={tail}
            onChange={(e) => {
              setTail(e.target.value);
              setPage(1);
            }}
            allowClear
            style={{ width: 300 }}
          />
          <Select
            placeholder="Category"
            value={category}
            onChange={(v) => {
              setCategory(v);
              setPage(1);
            }}
            allowClear
            style={{ width: 200 }}
            options={AIRCRAFT_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
          />
          <Select
            placeholder="Operator"
            value={operatorId}
            onChange={(v) => {
              setOperatorId(v);
              setPage(1);
            }}
            allowClear
            style={{ width: 240 }}
            showSearch
            optionFilterProp="label"
            options={(operators.data?.items || []).map((o) => ({ value: o.id, label: o.name }))}
          />
          <Select
            placeholder="Status"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </Space>
        {/* Regression 28 Sep — every column has a width and the table scrolls
            inside its own container, so the PAGE never scrolls sideways (1211px
            at a 1024px window) and no column is squeezed to one letter per line
            (Category / Cancel deadline / Status were, at 1280 and 1440). */}
        <Table<Aircraft>
          className="no-header-break"
          scroll={{ x: AIRCRAFT_TABLE_WIDTH }}
          tableLayout="fixed"
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data?.items || []}
          pagination={{
            current: page,
            total: list.data?.total || 0,
            pageSize,
            onChange: setPage,
            showTotal: (total) => `${total} aircraft`,
          }}
          columns={[
            {
              title: 'Aircraft',
              key: 'aircraft',
              // Same trap as the operators list: without a column width the ellipsis
              // never engaged and long model names ran over the Category column.
              width: 210,
              render: (_, r) => (
                <div style={{ maxWidth: 180 }}>
                  <Text strong ellipsis={{ tooltip: r.modelName }} style={{ display: 'block' }}>{r.modelName}</Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis={{ tooltip: r.tailNumber }}>
                    {r.tailNumber}
                  </Text>
                </div>
              ),
            },
            {
              title: 'Category',
              dataIndex: 'category',
              key: 'category',
              width: 130,
              // Wraps between words ("Super midsize / jet") at its fixed width.
              render: (c: AircraftCategory) => CATEGORY_LABELS[c],
            },
            // SHAR-196 — column renamed Seating → Max Seats.
            { title: 'Max Seats', dataIndex: 'seatingCapacity', key: 'seats', width: 70 },
            // SHAR-194 — the Range (NM) column was removed from the table.
            // SHAR-195 — show year of manufacture and refurbishment.
            { title: 'Year of mfr.', dataIndex: 'yearOfManufacture', key: 'yom', width: 80, render: (y?: number) => y ?? '—', responsive: ['lg'] },
            { title: 'Year of refurb.', dataIndex: 'yearOfRefurbishment', key: 'yor', width: 90, render: (y?: number) => y ?? '—', responsive: ['lg'] },
            // SHAR-198 — Owner's approval column.
            {
              title: "Owner's approval",
              dataIndex: 'ownersApproval',
              key: 'ownersApproval',
              width: 100,
              render: (v?: boolean) =>
                v ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
            },
            {
              title: 'Operator',
              key: 'operator',
              width: 140,
              // The full name in a tooltip; the cell ellipsises within its width.
              render: (_, r) => (
                <Text ellipsis={{ tooltip: r.operator?.name }}>{r.operator?.name || '—'}</Text>
              ),
            },
            {
              title: 'Cancel deadline', // SHAR-471
              key: 'deadline',
              dataIndex: 'confirmationDeadlineDays',
              width: 100,
              render: (d: number | null) =>
                d == null ? '—' : `${d} day${d === 1 ? '' : 's'}`,
              responsive: ['lg'],
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (s: Status, r) => (
                <Space size={4} wrap>
                  <Tag color={s === 'active' ? 'green' : 'default'}>{s}</Tag>
                  {/* SHAR-458 — homebase airport was deleted; flag it so the admin
                      can reassign one without opening each aircraft. */}
                  {/* Regression 29 Sep - the tag may wrap: at one line it is 124px in a
                      68px column and ran into the actions column. */}
                  {r.homebaseMissing ? (
                    <Tag color="volcano" style={{ whiteSpace: 'normal', marginInlineEnd: 0 }}>
                      Missing homebase
                    </Tag>
                  ) : null}
                </Space>
              ),
            },
            {
              title: '',
              key: 'actions',
              width: 90,
              // Edit / Delete stay in view while the table scrolls sideways.
              fixed: 'right',
              render: (_, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => nav(`/aircraft/${r.id}`)} />
                  <Popconfirm
                    title="Delete aircraft"
                    description="Existing bookings will not be affected."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => deleteMut.mutate(r.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
